import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

// The LPGA fitting-system applications (Randall, 4 Nov 2026): the ten fields in a table,
// sortable, with the "how would you use it" text readable in full. Read-only: the choosing is
// Randall's, and it happens by reading, not by a button. Backend: lpga_api (/api/lpga/*).
const API = '/api';

const COLS = [
  ['received_at', 'Received'], ['name', 'Name'], ['member', 'LPGA #'], ['email', 'Email'], ['phone', 'Phone'],
  ['facility', 'Facility'], ['role', 'Role'], ['ftype', 'Facility type'], ['volume', 'Lessons / month'], ['sell', 'Can sell clubs'],
];

function fmt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
}

export default function LpgaApplications() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ key: 'received_at', dir: -1 });
  const [open, setOpen] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [a, s] = await Promise.all([axios.get(`${API}/lpga/applications`), axios.get(`${API}/lpga/status`)]);
        setRows(a.data?.applications || []);
        setStatus(s.data || null);
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load the applications.');
      }
      setLoading(false);
    })();
  }, []);

  const sorted = [...rows].sort((x, y) => {
    const a = String(x[sort.key] ?? '').toLowerCase();
    const b = String(y[sort.key] ?? '').toLowerCase();
    return a < b ? -sort.dir : a > b ? sort.dir : 0;
  });
  const clickSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === 'received_at' ? -1 : 1 }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="lpga-loading">
        <Loader2 className="animate-spin text-hg-green" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-400/30 text-red-300 rounded-sm px-4 py-3 text-sm font-sans" data-testid="lpga-error">
        {error}
      </div>
    );
  }

  const pageState = !status ? '' : !status.turnstile_keys_set
    ? 'The page is NOT open: TURNSTILE_SITE_KEY and TURNSTILE_SECRET_KEY must both be set on the backend service.'
    : !status.open ? 'Applications are closed.' : `Open until the end of ${new Date(status.closes_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} (Pacific).`;

  return (
    <div className="space-y-6" data-testid="lpga-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">LPGA Applications</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Applications to win a fitting system, from the cards in the gift bags at the LPGA Professionals Team Championship
          (The Wigwam, 5–6 December 2026). The page is hub.henry-griffitts.com/lpga; each one is emailed to Randall as it arrives.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lpga-counts">
        <div className="bg-hg-surface border border-hg-border rounded-sm p-3">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Applications</div>
          <div className="font-display text-2xl text-hg-text">{rows.length}</div>
        </div>
        <div className="bg-hg-surface border border-hg-border rounded-sm p-3 col-span-1 md:col-span-3">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Page</div>
          <div className={`font-sans text-sm mt-1 ${status && !status.turnstile_keys_set ? 'text-red-300' : 'text-hg-text'}`} data-testid="lpga-page-state">{pageState}</div>
        </div>
      </div>

      <div className="bg-hg-surface border border-hg-border rounded-sm p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sans" data-testid="lpga-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-hg-text-dim border-b border-hg-border">
                {COLS.map(([key, label]) => (
                  <th key={key} className="text-left py-2 pr-3 cursor-pointer select-none whitespace-nowrap" onClick={() => clickSort(key)}>
                    {label}{sort.key === key ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
                <th className="text-left py-2">How they would use it</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={COLS.length + 1} className="py-6 text-hg-text-dim">No applications yet.</td></tr>
              )}
              {sorted.map((r) => (
                <tr key={r.id} className="border-b border-hg-border/50 align-top">
                  {COLS.map(([key]) => (
                    <td key={key} className="py-2 pr-3 whitespace-nowrap text-hg-text">{key === 'received_at' ? fmt(r[key]) : r[key]}</td>
                  ))}
                  <td className="py-2 text-hg-text min-w-[20rem]">
                    {open === r.id ? (
                      <div className="whitespace-pre-wrap">{r.use}
                        <button type="button" className="ml-2 text-hg-green text-xs" onClick={() => setOpen(null)}>less</button>
                      </div>
                    ) : (
                      <div>{(r.use || '').slice(0, 140)}{(r.use || '').length > 140 ? '…' : ''}
                        {(r.use || '').length > 140 && <button type="button" className="ml-2 text-hg-green text-xs" onClick={() => setOpen(r.id)}>read all</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
